import { useState } from "react";
import { Guard } from "../types";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Phone, Mail, Copy, Camera, MessageCircle, Send, PhoneCall, Archive, Edit, Circle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { GuardExpandedInfo } from "./GuardExpandedInfo";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useTeamManagement } from "./TeamManagement/useTeamManagement";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ResponsiveGuardDetails } from "./ResponsiveGuardDetails";

interface GuardCardProps {
  guard: Guard;
  isAdmin: boolean;
  onGuardRemoved?: () => void;
}

export const GuardCard = ({ guard, isAdmin, onGuardRemoved }: GuardCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(guard.avatar_url);
  const { toast } = useToast();
  const { isAdmin: currentUserIsAdmin, hasRole } = useUserRoles();
  const isMobile = useIsMobile();

  const isGuard = hasRole('guard');
  const isClient = hasRole('client');
  const canViewDetails = currentUserIsAdmin && !isGuard && !isClient;

  const [guardDetailsOpen, setGuardDetailsOpen] = useState(false);

  const {
    teams,
    guardTeams,
    isLoading: isLoadingTeams,
    showConfirmDialog,
    pendingTeamAction,
    handleTeamAction,
    handleConfirmAction,
    setShowConfirmDialog,
    setPendingTeamAction
  } = useTeamManagement(guard, onGuardRemoved);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(guard.code || "");
    toast({
      title: "Copied",
      description: "Guard code copied",
    });
  };

  const handleCallGuard = () => {
    if (guard.mobile_phone) {
      window.open(`tel:${guard.mobile_phone}`);
    } else {
      toast({
        variant: "destructive",
        title: "No phone",
        description: "No mobile phone.",
      });
    }
  };

  const handleCallEmergencyContact = () => {
    if (guard.emergency_contact) {
      window.open(`tel:${guard.emergency_contact}`);
    } else {
      toast({
        variant: "destructive",
        title: "No emergency contact",
        description: "No emergency contact.",
      });
    }
  };

  const handleEmailGuard = () => {
    if (guard.email) {
      window.open(`mailto:${guard.email}`);
    } else {
      toast({
        variant: "destructive",
        title: "No email",
        description: "No email available.",
      });
    }
  };

  const handleWhatsAppGuard = () => {
    if (guard.mobile_phone) {
      const formattedPhone = guard.mobile_phone.replace(/[\s-]/g, '');
      window.open(`https://wa.me/${formattedPhone}`);
    } else {
      toast({
        variant: "destructive",
        title: "No phone",
        description: "No WhatsApp phone.",
      });
    }
  };

  const handleViberGuard = () => {
    if (guard.mobile_phone) {
      const formattedPhone = guard.mobile_phone.replace(/[\s-]/g, '');
      window.open(`viber://chat?number=${formattedPhone}`);
    } else {
      toast({
        variant: "destructive",
        title: "No phone",
        description: "No Viber phone.",
      });
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    const file = event.target.files[0];
    const fileExt = file.name.split('.').pop();
    const filePath = `${guard.id}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    setUploading(true);
    try {
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: publicUrlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      if (!publicUrlData?.publicUrl) throw new Error("Failed to get public URL");
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrlData.publicUrl })
        .eq('id', guard.id);
      if (updateError) throw updateError;
      setAvatarUrl(publicUrlData.publicUrl);
      toast({ title: "Profile photo updated" });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message || "Failed to upload photo",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancelTeamAction = () => {
    setShowConfirmDialog(false);
    setPendingTeamAction(null);
  };

  if (isGuard) {
    return (
      <Card
        key={guard.id}
        className="rounded-xl border-0 h-auto px-3 py-3 bg-card/80 shadow-sm hover:shadow-md transition-all duration-200"
        data-guard-id={guard.id}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-14 w-14 bg-white/70 ring-2 ring-blue-200/40 shadow-sm">
              <AvatarImage src={guard.avatar_url || `https://avatar.vercel.sh/${guard.last_name} ${guard.first_name}.png`} />
              <AvatarFallback className="bg-blue-50/80 text-blue-600 text-lg">👤</AvatarFallback>
            </Avatar>
          </div>
          <div className="flex-1">
          <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-base text-foreground truncate">
                  {guard.last_name} {guard.first_name}
                </span>
                <div className="flex items-center">
                  {guard.gender === 'male' ? (
                    <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                      ♂
                    </div>
                  ) : guard.gender === 'female' ? (
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex items-center justify-center text-white text-xs font-bold">
                      ♀
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap mt-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={handleCallGuard}
                        disabled={!guard.mobile_phone}
                        className="!min-w-0 px-2 h-8 bg-emerald-50/80 hover:bg-emerald-100/80 border-emerald-200/60 text-emerald-600"
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Call Guard</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={handleWhatsAppGuard}
                        disabled={!guard.mobile_phone}
                        className="!min-w-0 px-2 h-8 bg-emerald-50/80 hover:bg-emerald-100/80 border-emerald-200/60 text-emerald-600"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>WhatsApp</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={handleViberGuard}
                        disabled={!guard.mobile_phone}
                        className="!min-w-0 px-2 h-8 bg-violet-50/80 hover:bg-violet-100/80 border-violet-200/60 text-violet-600"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Viber</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={handleEmailGuard}
                        disabled={!guard.email}
                        className="!min-w-0 px-2 h-8 bg-sky-50/80 hover:bg-sky-100/80 border-sky-200/60 text-sky-600"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Email</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={handleCallEmergencyContact}
                        disabled={!guard.emergency_contact}
                        className="!min-w-0 px-2 h-8 bg-rose-50/80 hover:bg-rose-100/80 border-rose-200/60 text-rose-600"
                      >
                        <PhoneCall className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Emergency Call</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      key={guard.id}
      className={cn(
        "group rounded-xl shadow-sm border-2 h-[420px] px-3 py-4 relative flex flex-col bg-card/80 overflow-hidden transition-all hover:shadow-lg",
        guard.gender === 'male' && "border-blue-200 dark:border-blue-800",
        guard.gender === 'female' && "border-pink-200 dark:border-pink-800",
        !guard.gender && "border-border",
        guard.status === 'archived' && "border-rose-200/60 bg-card/60 opacity-80"
      )}
      data-guard-id={guard.id}
    >
      {guard.status === 'archived' && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none">
          <div className="rotate-[-18deg] bg-rose-100/90 text-rose-500 text-2xl font-bold px-8 py-1 rounded shadow-sm opacity-80 -translate-y-1/2">
            Archived
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-2">
        <div className="relative">
          <Avatar className="h-14 w-14 ring-2 ring-blue-200/50 shadow-sm">
            <AvatarImage src={avatarUrl || `https://avatar.vercel.sh/${guard.last_name} ${guard.first_name}.png`} />
            <AvatarFallback className="bg-blue-50/80 text-blue-600 text-lg">{guard.last_name?.[0]}{guard.first_name?.[0]}</AvatarFallback>
          </Avatar>
          {isAdmin && (
            <div className="absolute inset-0 bg-slate-800/20 opacity-0 group-hover:opacity-100 rounded-full flex items-center justify-center transition-opacity">
              <label htmlFor={`avatar-upload-${guard.id}`} className="cursor-pointer">
                <Camera className="h-5 w-5 text-white" />
                <input
                  type="file"
                  id={`avatar-upload-${guard.id}`}
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={uploading}
                />
              </label>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1">
            <span className="font-semibold text-base text-foreground truncate">
              {guard.last_name} {guard.first_name}
            </span>
            <div className="ml-1 flex items-center">
              {guard.gender === 'male' ? (
                <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                  ♂
                </div>
              ) : guard.gender === 'female' ? (
                <div className="w-4 h-4 rounded-full bg-pink-500 flex items-center justify-center text-white text-xs font-bold">
                  ♀
                </div>
              ) : null}
            </div>
            {(guard.user_roles?.[0]?.role || guard.Role) && (
              <Badge variant="outline" className="bg-blue-50/80 border-blue-200/60 text-blue-600 text-xs uppercase px-1.5 py-0.5">
                {(guard.user_roles?.[0]?.role || guard.Role) === 'guard' ? 'USER' : (guard.user_roles?.[0]?.role || guard.Role).toUpperCase()}
              </Badge>
            )}
            {guard.code && (
              <button
                type="button"
                onClick={handleCopyToClipboard}
                className="ml-1 text-xs hover:text-blue-500 transition text-slate-500"
                title="Copy code"
                tabIndex={-1}
              >
                <Copy className="inline-block h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {guard.email && (
              <span className="text-xs text-muted-foreground truncate">
                {guard.email}
              </span>
            )}
            {guard.afm && (
              <span className="text-xs ml-2 text-muted-foreground">VAT: {guard.afm}</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="xs"
                onClick={handleCallGuard}
                disabled={!guard.mobile_phone}
                className="!min-w-0 px-2 h-8 bg-emerald-50/80 hover:bg-emerald-100/80 border-emerald-200/60 text-emerald-600"
              >
                <Phone className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Call Guard<br/>{guard.mobile_phone || "-"}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="xs"
                onClick={handleCallEmergencyContact}
                disabled={!guard.emergency_contact}
                className="!min-w-0 px-2 h-8 bg-rose-50/80 hover:bg-rose-100/80 border-rose-200/60 text-rose-600"
              >
                <PhoneCall className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>Emergency:</span>
              <br />
              {guard.emergency_contact || "-"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="xs"
                onClick={handleEmailGuard}
                disabled={!guard.email}
                className="!min-w-0 px-2 h-8 bg-sky-50/80 hover:bg-sky-100/80 border-sky-200/60 text-sky-600"
              >
                <Mail className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>Email</span>
              <br />
              {guard.email || "-"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="xs"
                onClick={handleWhatsAppGuard}
                disabled={!guard.mobile_phone}
                className="!min-w-0 px-2 h-8 bg-emerald-50/80 hover:bg-emerald-100/80 border-emerald-200/60 text-emerald-600"
              >
                <Send className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>WhatsApp</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="xs"
                onClick={handleViberGuard}
                disabled={!guard.mobile_phone}
                className="!min-w-0 px-2 h-8 bg-violet-50/80 hover:bg-violet-100/80 border-violet-200/60 text-violet-600"
              >
                <MessageCircle className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Viber</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex flex-col gap-1 text-xs mb-3">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Code:</span>
          <span className="font-mono text-foreground">{guard.code}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Role:</span>
          <span className="text-foreground">
            {(guard.user_roles?.[0]?.role || guard.Role) === 'guard' ? 'user' : (guard.user_roles?.[0]?.role || guard.Role || "N/A")}
          </span>
        </div>
        {guard.hiring_date && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Hiring:</span>
            <span className="text-foreground">{guard.hiring_date && new Date(guard.hiring_date).toLocaleDateString("el-GR")}</span>
          </div>
        )}
        {guard.license_expiry_date && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">License Expiry:</span>
            <span className="text-foreground">{guard.license_expiry_date && new Date(guard.license_expiry_date).toLocaleDateString("el-GR")}</span>
          </div>
        )}
      </div>

      {/* Teams section with flexible height and scrolling */}
      <div className="flex-1 min-h-0 mb-3">
        <span className="text-xs text-muted-foreground">Teams:</span>
        {isLoadingTeams ? (
          <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
        ) : guardTeams && guardTeams.length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-1 max-h-16 overflow-y-auto">
            {guardTeams.map((team) => (
              <Badge
                key={team.id}
                variant="outline"
                className="bg-blue-50/80 border-blue-200/60 text-blue-600 px-1 py-0.5 text-xs font-medium"
              >
                {team.name}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="ml-2 text-xs text-muted-foreground">No team</span>
        )}
      </div>

      {/* Fixed bottom section */}
      <div className="mt-auto">
        <div className="flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={() => setExpanded((prev) => !prev)} size="xs" data-expand-button className="px-2 border border-border bg-muted hover:bg-muted/80 text-muted-foreground">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          {canViewDetails && (
            <>
              <Button 
                size={isMobile ? "xs" : "sm"} 
                variant="default" 
                onClick={() => setGuardDetailsOpen(true)} 
                className="px-3 py-1 rounded bg-blue-500/90 hover:bg-blue-600/90 text-white border-0"
              >
                <Edit className="h-4 w-4 mr-1" /> More
              </Button>
              
              {/* Always render the ResponsiveGuardDetails component */}
              <ResponsiveGuardDetails
                open={guardDetailsOpen}
                onOpenChange={setGuardDetailsOpen}
                guard={guard}
                onGuardRemoved={onGuardRemoved}
                title="Guard Details"
                description={`${guard.first_name} ${guard.last_name} - ${guard.code}`}
              />
            </>
          )}
        </div>
      </div>

      {/* Expandable section - positioned absolutely to not affect card height */}
      {expanded && (
        <div className="absolute inset-x-3 top-full mt-1 p-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200/40 z-30 text-xs">
          <div className="mb-2">
            <span className="font-medium text-slate-600">Date of Birth:</span>{" "}
            <span className="text-slate-500">
              {guard.date_of_birth
                ? new Date(guard.date_of_birth).toLocaleDateString("el-GR")
                : "-"}
            </span>
          </div>
          <div className="mb-2">
            <span className="font-medium text-slate-600">Emergency:</span>{" "}
            <span className="text-slate-500">{guard.emergency_contact || "-"}</span>
          </div>
          <div className="mb-2">
            <span className="font-medium text-slate-600">License Expiry:</span>{" "}
            <span className="text-slate-500">
              {guard.license_expiry_date
                ? new Date(guard.license_expiry_date).toLocaleDateString("el-GR")
                : "-"}
            </span>
          </div>
          {isAdmin && (
            <div className="flex gap-2 mt-2">
              <Button
                variant="outline"
                size="xs"
                className="border-rose-200/60 text-rose-500 hover:bg-rose-50/80 bg-rose-50/60"
                onClick={() => {
                  window.confirm("Archive guard?") && onGuardRemoved?.();
                }}
              >
                <Archive className="h-4 w-4" /> Archive
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
